import React, { Fragment } from 'react';

// === Components === //
import Container from '@components/common/Container';
import Accordion from '@components/common/Accordion';

// === Helpers === //
import * as utils from '@utils';

// === Styles === //
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { breakpoints, colors, dimensions, respondFrom, respondTo } from '@styles';

// === Types === //
import { Variant } from '@content/types/layout';

const HeaderWrapper = styled.div`
  display: flex;
  background-color: ${colors.ui.patientDefault};

  &.variant--red {
    background-color: ${colors.ui.hcpDefault};
  }
`;

const Cell = styled.div`
  color: ${colors.text.light};
  font-size: ${dimensions.fontSize.small}px;
  line-height: 16px;

  &.header {
    color: ${colors.white};
    font-size: ${dimensions.fontSize.regular}px;
    font-weight: 700;
  }

  &.cell {
    &--single {
      flex: 2;
      padding: 12px 22px;
    }

    &--double {
      flex: 6;
      padding: 7px 0;

      .cell-row {
        display: flex;

        > div {
          flex: 1;
          padding: 5px 22px;
        }
      }
    }

    p {
      margin: 0;
    }
  }
`;

const BodyWrapper = styled.div``;

const Row = styled.div`
  display: flex;

  &:nth-of-type(odd) {
    background-color: ${colors.ui.tableRow};
  }
`;

const AccordionContent = styled.div``;

const AccordionContentHeader = styled.div<AccordionContentHeaderProps>`
  display: flex;
  background-color: ${(props) =>
    props.variant === 'red' ? colors.ui.accordionHeader : colors.ui.patientDark};
  margin-left: -16px;
  width: calc(100% + 32px);
  margin-top: -9px;
  margin-bottom: 9px;

  > div {
    flex: 1;
    color: ${colors.white};
    padding: 8px 16px;
    font-size: ${dimensions.fontSize.tiny}px;
    line-height: 16px;
    font-weight: 700;
  }
`;

const AccordionContentBody = styled.div`
  .accordion-body__row {
    display: flex;

    &--cell {
      flex: 1;
      font-size: ${dimensions.fontSize.tiny}px;
      line-height: 16px;
      padding: 4px 0;
      padding-right: 16px;
      border-right: 1px solid ${colors.ui.tableBorder};

      &:last-of-type {
        padding-right: 0;
        padding-left: 16px;
        border: none;
      }

      p {
        margin: 0;
      }
    }
  }
`;

const DesktopView = styled.div`
  ${respondTo(
    breakpoints.md,
    css`
      display: none;
    `
  )}
`;
const MobileView = styled.div`
  ${respondFrom(
    breakpoints.md,
    css`
      display: none;
    `
  )}
`;

interface TableProps {
  header: string[];
  body: string[][] | string[];
  variant: Variant;
}

interface AccordionContentHeaderProps {
  variant: Variant;
}

const Table = ({ header, body, variant }: TableProps) => {
  return (
    <Container>
      <>
        <DesktopView>
          <TableDesktop header={header} body={body} variant={variant} />
        </DesktopView>
        <MobileView>
          <TableMobile header={header} body={body} variant={variant} />
        </MobileView>
      </>
    </Container>
  );
};

const TableDesktop = ({ header, body, variant }: TableProps) => {
  return (
    <div>
      <HeaderWrapper className={`variant--${variant}`}>
        <Cell className={`header cell cell--single`}>{header[0]}</Cell>
        <Cell className={`header cell cell--double`}>
          <div className="cell-row">
            <div>{header[1]}</div>
            <div>{header[2]}</div>
          </div>
        </Cell>
      </HeaderWrapper>
      <BodyWrapper>
        {body.map((row, rowIndex) => {
          return (
            <Row key={rowIndex}>
              <Cell className={`cell cell--single`}>{utils.SafeHtml(row[0])}</Cell>
              <Cell className={`cell cell--double`}>
                {row[1].map((cell, cellIndex) => (
                  <div key={cellIndex} className="cell-row">
                    {cell.map((c, cIndex) => (
                      <div key={cIndex}>{utils.SafeHtml(c)}</div>
                    ))}
                  </div>
                ))}
              </Cell>
            </Row>
          );
        })}
      </BodyWrapper>
    </div>
  );
};

const TableMobile = ({ header, body, variant }: TableProps) => {
  return (
    <div>
      {body.map((row, rowIndex) => (
        <Accordion
          key={rowIndex}
          title={row[0]}
          variant={variant}
          id={`accordion-${rowIndex}`}
          preExpanded={[`accordion-0`]}
        >
          <AccordionContent>
            <AccordionContentHeader variant={variant}>
              {header.map((header, headerIndex) => (
                <Fragment key={headerIndex}>
                  {headerIndex !== 0 && <div>{utils.SafeHtml(header)}</div>}
                </Fragment>
              ))}
            </AccordionContentHeader>
            <AccordionContentBody>
              {row[1].map((cell, cellIndex) => {
                return (
                  <div key={cellIndex} className="accordion-body__row">
                    {cell.map((c, cIndex) => (
                      <div key={cIndex} className="accordion-body__row--cell">
                        {utils.SafeHtml(c)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </AccordionContentBody>
          </AccordionContent>
        </Accordion>
      ))}
    </div>
  );
};

export default Table;
